<script setup lang="ts">
/**
 * 공통 DCR 인트로 — 실제 템플릿 문구 그대로 (OtcDcrIntro 미러링):
 *   [[if is_sole_director_company]]I, the undersigned, being the sole director…[[endif]]
 *   [[if is_multiple_director_company]]We, the undersigned, being the directors…[[endif]]
 *   pursuant to [[if resolution_basis_constitution]]Clause [[clause]] of the Company's Constitution[[endif]] …
 * · sole/multiple 은 패널이 entity 이사 수로 자동(is_multiple_director_company). 수동 토글 override.
 * · Clause/Article No 는 entity 등록 시 자동+잠금(🔒), 미등록이면 ✨ 클릭 → AI 챗봇 clause 위젯.
 */
import { computed, inject } from 'vue';
import { TRANSFER_DOC_CTX } from '../transferContext';

const ctx = inject(TRANSFER_DOC_CTX)!;
const isMultiple = computed(() => ctx.flag('is_multiple_director_company'));
const basis = computed(() => ctx.choiceOf('resolution_basis') || 'constitution');
const locked = computed(() => ctx.clauseLocked.value);
const clauseNo = computed(() => ctx.valueOf('constitution_clause_number'));

function cycleBasis() {
  if (locked.value) return;
  const next = basis.value === 'constitution' ? 'articles' : basis.value === 'articles' ? 'third_schedule' : 'constitution';
  ctx.setChoice('resolution_basis', next);
}
</script>

<template>
  <div class="dcr-title">DIRECTORS' CIRCULAR RESOLUTION</div>
  <p class="doc-p">
    <span class="doc-toggle" title="sole / multiple director (auto from entity)" @click="ctx.toggleFlag('is_multiple_director_company')">{{ isMultiple ? 'We, the undersigned, being the directors' : 'I, the undersigned, being the sole director' }}</span>
    of the above-mentioned Company for the time being, do resolve the following in writing pursuant to

    <template v-if="basis === 'third_schedule'">
      <span class="doc-toggle" :class="{ locked }" :title="locked ? 'Registered — locked' : 'change resolution basis'" @click="cycleBasis">Paragraph 15 of the Third Schedule of the Companies Act 2016</span>.
    </template>
    <template v-else>
      <span class="doc-toggle" :class="{ locked }" :title="locked ? 'Registered — locked' : 'change resolution basis'" @click="cycleBasis">{{ basis === 'articles' ? 'Article' : 'Clause' }}</span>
      <span v-if="locked" class="trf-clause trf-clause--lk" title="Registered on entity — locked">{{ clauseNo || '—' }}<v-icon size="11" class="trf-clause-lock">mdi-lock</v-icon></span>
      <span v-else class="trf-clause trf-clause--pick" :class="{ empty: !clauseNo }" title="Pick from the AI assistant" @click="ctx.activate('constitution_clause_number')">{{ clauseNo || 'Clause / Article No.' }}<v-icon size="12" class="trf-clause-ai">mdi-creation</v-icon></span>
      <template v-if="basis === 'articles'"> of the Company's Constitution (Articles of Association)</template>
      <template v-else> of the Company's Constitution</template>.
    </template>
  </p>
</template>

<style scoped>
.dcr-title { text-align:center; font-weight:800; letter-spacing:.02em; margin:14px 0 8px; }
.doc-p { margin:0 0 10px; line-height:1.55; }
.doc-toggle.locked { background:#eef2ff; box-shadow:inset 0 -2px 0 #9aa8d8; cursor:default; }
.trf-clause { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 4px; vertical-align:baseline; font-weight:600; }
.trf-clause--lk { background:#eef2ff; box-shadow:inset 0 -2px 0 #9aa8d8; color:#334; cursor:default; }
.trf-clause-lock { color:#7684bf; }
.trf-clause--pick { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; color:#10305c; cursor:pointer; }
.trf-clause--pick.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; color:#9a7b1a; font-style:italic; font-weight:400; }
.trf-clause-ai { color:#1B84FF; }
.trf-clause--pick:hover { filter:brightness(.97); }
</style>
